import React, { useRef, useEffect } from 'react';
import { CheckboxToggleRefinement } from './CheckboxToggleRefinement';
import { useToggleRefinement } from 'react-instantsearch';
import qs from 'qs';
import { updateSearchRefFromUrl } from '../utils';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { getSession, SessionStorageKeys } from '@jbhi-fi/utils/src/sessionStorage';
import type { SearchState } from '../../collections/searchStateUrlStorageHelpers';
import type { PriceRangeTrackingInfo } from '../PriceRangeSlider/PriceRangeSlider';

interface CheckboxSaleItemsProps {
  label: string;
  setIsSaleItemsRefined: React.Dispatch<React.SetStateAction<boolean>>;
  nbSaleItems: number;
  trackingInfo: PriceRangeTrackingInfo;
}

const CheckboxSaleItems = ({ label, setIsSaleItemsRefined, nbSaleItems, trackingInfo }: CheckboxSaleItemsProps) => {
  const { value, refine } = useToggleRefinement({ attribute: 'onPromotion' });
  const isCurrentRefinement = !!value?.isRefined;
  const searchState = qs.parse(window.location.search.slice(1)) as SearchState;
  const searchRef = useRef(searchState);

  useEffect(() => {
    setIsSaleItemsRefined(isCurrentRefinement);
  }, [isCurrentRefinement]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isCurrentRefinement = event.target.checked;
    const filters = Object.keys(searchState)
      .filter(key => !['query', 'page', 'hitsPerPage', 'sortBy'].includes(key))
      .reduce((obj: any, key: string) => {
        const keyLowerCase = key.toLowerCase();
        if (keyLowerCase === 'price') {
          obj[keyLowerCase] = { type: 'range', value: searchState[key] };
        } else {
          obj[keyLowerCase] = Array.isArray(searchState[key])
            ? (searchState[key] as Array<string>).map(i => ({ type: 'checkbox', value: i }))
            : { type: 'checkbox', value: searchState[key] };
        }
        return obj;
      }, {});

    refine({ isRefined: !isCurrentRefinement });

    updateSearchRefFromUrl(searchRef);

    pushToDataLayer({
      event: isCurrentRefinement ? 'select_filter' : 'unselect_filter',
      filter_group: 'Price',
      filter_name: 'sale items',
      filter_type: 'checkbox',
      filter_value: 'sale items',
      search_type: getSession(SessionStorageKeys.SearchMethod) || 'site search',
      search_term: searchState?.query || '',
    });

    window.dataLayer.push({
      event: 'custom.filter.set',
      group: 'site search',
      category: 'search',
      label: isCurrentRefinement ? 'on' : 'off',
      name: 'saleItems',
      value: isCurrentRefinement,
      info: {
        element: 'checkbox',
        position: trackingInfo.position,
        sortyBy: searchRef.current.sortBy || trackingInfo.defaults.sortBy,
        hitsPerPage: Number(searchRef.current.hitsPerPage) || trackingInfo.defaults.hitsPerPage,
      },
      newFilters: {
        ...filters,
        saleItems: {
          type: 'checkbox',
          value: isCurrentRefinement ? 'on' : 'off',
        },
      },
      oldFilters: filters,
    });
  };

  if (nbSaleItems === 0) return null;

  return (
    <CheckboxToggleRefinement
      label={label}
      isChecked={isCurrentRefinement}
      onChange={onChange}
      count={nbSaleItems}
      id="saleItemsRefinement"
    />
  );
};

export default CheckboxSaleItems;
